import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable } from 'rxjs';
import { StorageVariables } from '../../shared/constants/app.enum';
import { NotificationService } from '../../shared/services/notification.service';
import { StorageService } from '../services/storage.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly jwtHelperService = inject(JwtHelperService);
  private readonly notification = inject(NotificationService);
  private readonly storageService = inject(StorageService);
  private readonly router = inject(Router);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const token = this.getToken();

    if (token && this.isJWTValid() && !this.isJWTExpired()) {
      request = this.addAuthorizationHeader(request, token);
    }

    return next.handle(request);
  }

  private getToken(): string | null {
    return this.storageService.get(StorageVariables.TOKEN);
  }

  private addAuthorizationHeader(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private isJWTValid(): boolean {
    try {
      this.jwtHelperService.decodeToken(this.getToken()!);
      return true;
    } catch (e) {
      this.handleSessionExpiration();
      return false;
    }
  }

  private isJWTExpired(): boolean {
    const isExpired = this.jwtHelperService.isTokenExpired(this.getToken());

    if (isExpired) {
      this.handleSessionExpiration();
      return true;
    }
    return false;
  }

  private handleSessionExpiration() {
    this.storageService.clear().subscribe(() => {
      this.notification.warning('Session expired, please login again to continue');

      setTimeout(() => this.router.navigate(['/auth/login']));
    });
  }
}
